import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Phone, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import SectionHeading from "../components/SectionHeading";
import { api } from "../lib/api";

const initial = { name: "", email: "", phone: "", subject: "", message: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailSent, setEmailSent] = useState(true);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!EMAIL_RE.test(form.email.trim())) return "Please enter a valid email address.";
    if (!form.message.trim()) return "Please enter a message.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return; // hard guard against double-submit
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/contacts", form);
      const ok = !!res?.data?.email_sent;
      setEmailSent(ok);
      setSent(true);
      setForm(initial);
      if (ok) {
        toast.success("Submission received successfully.");
      } else {
        toast.success(
          "Your submission was received. Our team will review it soon."
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send. Please try again.";
      console.error("[contact] submit failed:", err);
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#F5EFE0] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading
          eyebrow="Get In Touch"
          title="Contact The Titans"
          description="Questions about registration, practice, or volunteering? Drop us a line."
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* INFO */}
          <aside className="lg:col-span-2 space-y-6">
            <InfoBlock icon={Mail} label="Main Email" lines={[{ text: "Cawstontitans@gmail.com", href: "mailto:Cawstontitans@gmail.com" }]} testid="contact-info-main-email" highlight />
            <InfoBlock icon={MapPin} label="Practice Location" lines={["Rancho Viejo Middle School", "985 Cawston Ave N", "Hemet, CA 92545"]} testid="contact-info-location" />
            <InfoBlock
              icon={Phone}
              label="Coaching Staff"
              testid="contact-info-coaches"
              lines={[
                { text: "Damon Johnson · Board / 10U — (951) 401-3498", href: "tel:9514013498" },
                { text: "William Butler · VP — (951) 442-7614", href: "tel:9514427614" },
                { text: "Coach AD · 8U HC — (619) 362-1441", href: "tel:6193621441" },
                { text: "Coach Jones · 12U/13U HC — (951) 750-4018", href: "tel:9517504018" },
                { text: "Coach Myles · 12U/13U AC — (951) 590-1675", href: "tel:9515901675" },
                { text: "Coach Darryl · 10U HC — (951) 728-3987", href: "tel:9517283987" },
                { text: "Coach Roc · 8U HC — (323) 392-9669", href: "tel:3233929669" },
                { text: "Coach Walker · 8U AC — (951) 404-4595", href: "tel:9514044595" },
              ]}
            />
          </aside>

          {/* FORM / SUCCESS */}
          <div className="lg:col-span-3">
            {sent ? (
              <div data-testid="contact-success" className="bg-white border border-[#2D5F3F]/40 p-10 text-center">
                <div className="inline-flex w-16 h-16 bg-[#2D5F3F] items-center justify-center shadow-ink-offset mb-6">
                  <CheckCircle2 className="w-8 h-8 text-[#FDFBF7]" strokeWidth={2.5} />
                </div>
                <h3 className="font-display text-3xl font-black uppercase text-[#0A0A0A]">
                  {emailSent ? "Submission received!" : "Submission saved"}
                </h3>
                <p className="mt-4 text-black/75">
                  {emailSent
                    ? "Submission received successfully. We'll get back to you soon."
                    : "Your submission was received. Our team will review it soon."}
                </p>
                <button
                  onClick={() => setSent(false)}
                  data-testid="contact-send-another"
                  className="mt-8 inline-flex items-center gap-2 bg-[#2D5F3F] hover:bg-[#1B4332] text-[#FDFBF7] font-display uppercase tracking-wider font-extrabold px-6 py-3 transition-colors"
                >
                  Send Another <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-black/10 p-6 sm:p-10 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-[#2D5F3F] text-xs font-bold uppercase tracking-[0.2em] mb-2 block">Name *</Label>
                    <Input required data-testid="contact-input-name" value={form.name} onChange={update("name")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-[#2D5F3F] text-xs font-bold uppercase tracking-[0.2em] mb-2 block">Email *</Label>
                    <Input required data-testid="contact-input-email" type="email" value={form.email} onChange={update("email")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-[#2D5F3F] text-xs font-bold uppercase tracking-[0.2em] mb-2 block">Phone</Label>
                    <Input data-testid="contact-input-phone" type="tel" value={form.phone} onChange={update("phone")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-[#2D5F3F] text-xs font-bold uppercase tracking-[0.2em] mb-2 block">Subject</Label>
                    <Input data-testid="contact-input-subject" value={form.subject} onChange={update("subject")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#2D5F3F] text-xs font-bold uppercase tracking-[0.2em] mb-2 block">Message *</Label>
                  <Textarea required rows={6} data-testid="contact-input-message" value={form.message} onChange={update("message")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-2 bg-[#2D5F3F] hover:bg-[#1B4332] disabled:opacity-60 text-[#FDFBF7] font-display uppercase tracking-wider font-extrabold px-8 py-4 shadow-ink-offset hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : <>Send Message <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoBlock({ icon: Icon, label, lines, testid, highlight = false }) {
  return (
    <div data-testid={testid} className={`p-6 transition-colors ${highlight ? "bg-[#2D5F3F] text-[#FDFBF7] border border-[#2D5F3F]" : "bg-white border border-black/10 hover:border-[#2D5F3F]/50"}`}>
      <div className={`flex items-center gap-3 ${highlight ? "text-[#FDFBF7]" : "text-[#2D5F3F]"}`}>
        <Icon className="w-5 h-5" />
        <span className="font-display text-xs uppercase tracking-[0.3em] font-bold">{label}</span>
      </div>
      <div className={`mt-4 space-y-2 ${highlight ? "text-[#FDFBF7]" : "text-black/85"}`}>
        {lines.map((l, i) =>
          typeof l === "string" ? (
            <div key={i} className="leading-relaxed">{l}</div>
          ) : (
            <a key={i} href={l.href} className={`block transition-colors break-all ${highlight ? "hover:underline font-bold text-lg" : "hover:text-[#2D5F3F]"}`}>{l.text}</a>
          )
        )}
      </div>
    </div>
  );
}
